import { RemoteListDataSource } from "./RemoteListDataSource";
import { RemoteOneDataSource } from "./RemoteOneDataSource";
import {
  pathVariableDtoStrategy,
  pathVariableParentChangeStrategy
} from "./ChildDataSourceStrategies";
import { action } from "mobx";
import { FindAllParams } from "./DataSource";
import { PageableResult } from "./Api";
import {
  AnyChildDataSource,
  RemoteChildDataSourceOptions
} from "./RemoteChildDataSource";
import { Lock } from "../helpers/asyncUtils";
import { isEqual } from "lodash";
import { LoggerFactory } from "../logger";

export class RemoteListChildDataSource<T, ID> extends RemoteListDataSource<
  T,
  ID
> {
  public readonly parent: RemoteOneDataSource<any, any>;
  public lock: Lock;
  public readonly urlSuffix: string;
  private readonly parentChangedStrategy: (ds: AnyChildDataSource) => void;
  private readonly dtoStrategy: (ds: AnyChildDataSource, dto: any) => any;
  private prevParentId: any;

  public constructor(options: RemoteChildDataSourceOptions) {
    super(options);
    this.urlSuffix = options.url;
    this.lock = new Lock(this.name, true);
    this.log = LoggerFactory.create(`RemoteListChildDataSource [${this.name}]`);
    this.parentChangedStrategy =
      options.parentChangedStrategy || pathVariableParentChangeStrategy();
    this.dtoStrategy = options.dtoStrategy || pathVariableDtoStrategy();
    this.parent = options.parent;
    this.parent.onEvent("one.load", () => this.onParentChanged());
    this.parent.onEvent("one.clear", () => this.onParentChanged());
    this.onParentChanged();
  }

  @action
  public onParentChanged() {
    this.log.debug("onParentChanged", this.parent.id);

    if (isEqual(this.prevParentId, this.parent.id)) {
      this.log.debug("ignoring event because parent didnt change");
      return;
    }

    this.prevParentId = this.parent.id;
    this.parentChangedStrategy(this);
    this.clear();
    this.triggerEvent("parentChanged");
  }

  @action
  public async findAll(
    params: FindAllParams,
    force: boolean = false
  ): Promise<PageableResult<T>> {
    await this.lock.waitForUnlock();
    const parentIdString = JSON.stringify(this.parent.id);

    this.log.debug(`findAll with parent: ${parentIdString}`);
    return super.findAll(params, force);
  }

  @action
  protected findAllSuccess(response: PageableResult<T>): PageableResult<T> {
    return super.findAllSuccess({
      ...response,
      content: response.content.map(row => this.dtoStrategy(this, row))
    });
  }

  @action
  public async updateRows(
    ids: ID[] | ID,
    data: {},
    config?: {}
  ): Promise<void> {
    await this.lock.waitForUnlock();
    const parentIdString = JSON.stringify(this.parent.id);

    this.log.debug(`updateRows with parent: ${parentIdString}`);
    data = this.dtoStrategy(this, data);
    return super.updateRows(ids, data, config);
  }

  @action
  public async delete(ids: ID[] | ID): Promise<void> {
    await this.lock.waitForUnlock();
    const idString = JSON.stringify(ids);
    const parentIdString = JSON.stringify(this.parent.id);

    this.log.debug(
      `delete with parent: ${parentIdString} and ids: ${idString}`
    );
    return super.delete(ids);
  }

  @action
  public async moveRow(
    row: T,
    data: { moveTo: number },
    config?: {}
  ): Promise<void> {
    await this.lock.waitForUnlock();
    const parentIdString = JSON.stringify(this.parent.id);
    this.log.debug(`moveRow with parent: ${parentIdString}`);
    return super.moveRow(row, data, config);
  }
}
